import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, TemplateRef, HostListener, ChangeDetectorRef, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TableComponent, TableColumn, PaginationConfig, ModalComponent, DateRangePickerComponent, DateRange } from '../../../shared/components/primeng';
import { ActionDropdownTriggerDirective } from '../../../shared/directives/action-dropdown-trigger.directive';
import { AclApdApiService, istToUtc } from '@angular-workspace/services';
import { ToasterService } from '../../../services/toaster.service';
import { ActionDropdownOverlayController } from '../../../shared/utils/action-dropdown-overlay.sync';
import { SingleRowExpansionState } from '../../../shared/utils/single-row-expansion.util';
import { displayValueOrDash } from '../../../shared/utils/display-value.util';
import { FormatService } from '../../../shared/services/format.service';
import {
  ConversationService,
  canOpenConversation,
  isAccessStillActive,
} from '../../../shared/services/conversation.service';
import { WorkspaceContextService } from '../../../services/workspace-context.service';

export interface AssetRequestData {
  assetName: string;
  assetType: string;
  requestType: string;
  assetStatus: string;
  requestedBy: string;
  consumerEmail: string;
  organisationName: string;
  purpose: string;
  requestedAt: string;
  expiresAt: string;
  action: string;
  originalData?: any;
}

type RequestStatusFilter = 'ALL' | 'PENDING' | 'REJECTED' | 'WITHDRAWN' | 'GRANTED' | 'EXPIRED' | 'REVOKED';

@Component({
  selector: 'app-asset-request',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableComponent,
    ModalComponent,
    DateRangePickerComponent,
    ActionDropdownTriggerDirective,
  ],
  templateUrl: './asset-request.component.html',
  styleUrl: './asset-request.component.scss',
})
export class AssetRequestComponent implements OnInit, OnDestroy, AfterViewInit {
  /** Route prefix for the request conversation page; the action is hidden when unset. */
  @Input() conversationRoute: string | null = null;
  @ViewChild('assetRequestActionCell', { static: false }) assetRequestActionCellTemplate!: TemplateRef<any>;
  @ViewChild('assetRequestDateCell', { static: false }) assetRequestDateCellTemplate!: TemplateRef<any>;
  @ViewChild('assetRequestStatusCell', { static: false }) assetRequestStatusCellTemplate!: TemplateRef<any>;
  @ViewChild('assetRequestExpandCell', { static: false }) assetRequestExpandCellTemplate!: TemplateRef<any>;
  @ViewChild('assetRequestChildRow', { static: false }) assetRequestChildRowTemplate!: TemplateRef<any>;

  constructor(
    private aclApdApiService: AclApdApiService,
    private router: Router,
    private route: ActivatedRoute,
    private toasterService: ToasterService,
    private cdr: ChangeDetectorRef,
    private formatService: FormatService
  ) {}

  private readonly conversationService = inject(ConversationService);
  private readonly workspaceContext = inject(WorkspaceContextService);

  private readonly assetRequestBaseColumns: TableColumn[] = [
    { key: 'expand', label: '', sortable: false, width: '44px', fixed: 'left' },
    { key: 'assetName', label: 'Asset Name', sortable: false, width: '280px' },
    { key: 'assetType', label: 'Asset Type', sortable: false, width: '130px' },
    { key: 'requestType', label: 'Request Type', sortable: false, width: '130px' },
    { key: 'requestedBy', label: 'Requested By', sortable: false, width: '160px' },
    { key: 'requestDate', label: 'Requested At', sortable: true, width: '176px' },
  ];

  private readonly assetRequestActionColumn: TableColumn = {
    key: 'action',
    label: 'Action',
    sortable: false,
    fixed: 'right',
    width: '120px',
  };

  assetRequestColumns: TableColumn[] = [];

  assetRequestData: AssetRequestData[] = [];
  assetRequestPagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true,
  };
  assetRequestLoading = false;
  assetRequestEmptyMessage = 'No asset requests found';

  // Sort configuration — newest requests first by default
  currentSort: { field: string; order: 'asc' | 'desc' } = { field: 'requestDate', order: 'desc' };
  selectedRequestStatus: RequestStatusFilter = 'ALL';
  readonly requestStatusTabs: Array<{ label: string; value: RequestStatusFilter }> = [
    { label: 'All', value: 'ALL' },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Rejected', value: 'REJECTED' },
    { label: 'Withdrawn', value: 'WITHDRAWN' },
    { label: 'Granted', value: 'GRANTED' },
    { label: 'Expired', value: 'EXPIRED' },
    { label: 'Revoked', value: 'REVOKED' },
  ];

  openDropdownId: string | null = null;
  dropdownPosition: { top: number; left: number | null; right: number | null } = {
    top: 0,
    left: null,
    right: null,
  };
  
  // Track expanded row for child row functionality (one at a time)
  private readonly rowExpansion = new SingleRowExpansionState();

  // Approve modal state
  isApproveModalVisible = false;
  selectedRequest: AssetRequestData | null = null;
  expirationDate: DateRange | null = null;
  minDate: Date = new Date();

  ngOnInit(): void {
    this.applyRequestStatusFromUrl();
    this.syncAssetRequestColumns();
    this.loadAssetRequests();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.syncAssetRequestColumns();
      this.cdr.detectChanges();
    }, 100);
  }

  /** Rebuild columns: Status on All tab; date label varies by status tab. */
  private syncAssetRequestColumns(): void {
    const cols = this.assetRequestBaseColumns.map((col) => ({ ...col }));
    if (this.selectedRequestStatus === 'ALL') {
      const requestTypeIdx = cols.findIndex((col) => col.key === 'requestType');
      cols.splice(requestTypeIdx + 1, 0, {
        key: 'assetStatus',
        label: 'Status',
        sortable: true,
        width: '130px',
      });
    }
    const dateColumn = cols.find((col) => col.key === 'requestDate');
    if (dateColumn) {
      dateColumn.label =
        this.selectedRequestStatus === 'EXPIRED'
          ? 'Expired At'
          : this.selectedRequestStatus === 'GRANTED'
            ? 'Expires At'
            : 'Requested At';
    }
    cols.push({ ...this.assetRequestActionColumn });
    this.assetRequestColumns = cols;
    this.bindAssetRequestColumnTemplates();
  }

  private bindAssetRequestColumnTemplates(): void {
    if (this.assetRequestExpandCellTemplate) {
      const expandColumn = this.assetRequestColumns.find((col) => col.key === 'expand');
      if (expandColumn) {
        expandColumn.cellTemplate = this.assetRequestExpandCellTemplate;
      }
    }
    if (this.assetRequestActionCellTemplate) {
      const actionColumn = this.assetRequestColumns.find((col) => col.key === 'action');
      if (actionColumn) {
        actionColumn.cellTemplate = this.assetRequestActionCellTemplate;
      }
    }
    if (this.assetRequestDateCellTemplate) {
      const requestDateColumn = this.assetRequestColumns.find((col) => col.key === 'requestDate');
      if (requestDateColumn) {
        requestDateColumn.cellTemplate = this.assetRequestDateCellTemplate;
      }
    }
    if (this.assetRequestStatusCellTemplate) {
      const statusColumn = this.assetRequestColumns.find((col) => col.key === 'assetStatus');
      if (statusColumn) {
        statusColumn.cellTemplate = this.assetRequestStatusCellTemplate;
      }
    }
  }

  /** Map raw asset type to display label (e.g. adex:DataBank -> Dataset). */
  getAssetTypeDisplayLabel(rawType: string): string {
    if (!rawType || typeof rawType !== 'string') return '';
    const t = rawType.toLowerCase();
    if (t.includes('databank') || t.includes('database')) return 'Dataset';
    if (t.includes('aimodel') || t.includes('model')) return 'Model';
    if (t.includes('app') || t.includes('usecase')) return 'App';
    return rawType;
  }

  private extractDateString(input: unknown): string {
    if (!input) return '';
    if (typeof input === 'string' || typeof input === 'number') return String(input);
    if (typeof input === 'object') {
      const obj = input as Record<string, unknown>;
      const nested =
        obj['value'] ??
        obj['@value'] ??
        obj['date'] ??
        obj['dateTime'] ??
        obj['datetime'] ??
        obj['timestamp'];
      if (nested != null) return String(nested);
    }
    return '';
  }

  /** Format date-time parts in IST for date + greyed timestamp display. */
  getDateTimeParts(
    dateValue: unknown,
    assumeIstWhenNoTimezone = false,
  ): { date: string; time: string } {
    const raw = this.extractDateString(dateValue);
    if (!raw) return { date: '-', time: '' };
    const formatted = this.formatService.formatDate(raw, assumeIstWhenNoTimezone);
    if (!formatted) return { date: raw, time: '' };
    const spaceIdx = formatted.indexOf(' ');
    if (spaceIdx === -1) return { date: formatted, time: '' };
    return { date: formatted.slice(0, spaceIdx), time: formatted.slice(spaceIdx + 1) };
  }

  getDateCellValue(row: AssetRequestData): { date: string; time: string } {
    const showExpiresAt =
      this.selectedRequestStatus === 'GRANTED' ||
      this.selectedRequestStatus === 'EXPIRED';
    const rawDate = showExpiresAt ? row.expiresAt : row.requestedAt;
    return this.getDateTimeParts(rawDate);
  }

  getStatusActorEntry(row: AssetRequestData): { label: string; value: string } | null {
    const status = (row?.assetStatus ?? '').toString().toLowerCase();
    if (status === 'granted' || status === 'approved') {
      return { label: 'Approved by', value: this.resolveStatusActorName(row.originalData, 'approved') };
    }
    if (status === 'rejected') {
      return { label: 'Rejected by', value: this.resolveRejectedByDisplayName(row.originalData) };
    }
    if (status === 'revoked') {
      return { label: 'Revoked by', value: this.resolveStatusActorName(row.originalData, 'revoked') };
    }
    return null;
  }

  /** Name of the person who rejected the request. */
  private resolveRejectedByDisplayName(item: any): string {
    const sources = [item, item?.asset, item?.additionalInfo].filter(
      (src) => src && typeof src === 'object',
    );
    for (const source of sources) {
      const candidates = [source.rejectedBy, source.rejected_by, source];
      for (const candidate of candidates) {
        if (!candidate || typeof candidate !== 'object') continue;
        const person = candidate as Record<string, unknown>;
        const named = this.formatActorValue({
          rejectedByFirstName:
            person['rejectedByFirstName'] ?? person['rejected_by_first_name'],
          rejectedByLastName:
            person['rejectedByLastName'] ?? person['rejected_by_last_name'],
        });
        if (named !== '-') return named;
      }
    }
    return this.resolveStatusActorName(item, 'rejected');
  }

  private resolveStatusActorName(
    item: any,
    kind: 'approved' | 'rejected' | 'revoked',
  ): string {
    if (!item || typeof item !== 'object') return '-';
    const keySets: Record<'approved' | 'rejected' | 'revoked', string[]> = {
      approved: [
        'approvedBy',
        'approved_by',
        'grantedBy',
        'granted_by',
        'approvedByName',
        'approved_by_name',
        'approvedByUsername',
        'approved_by_username',
      ],
      rejected: [
        'rejectedBy',
        'rejected_by',
        'rejectedByName',
        'rejected_by_name',
        'rejectedByUsername',
        'rejected_by_username',
      ],
      revoked: [
        'revokedBy',
        'revoked_by',
        'revokedByName',
        'revoked_by_name',
        'revokedByUsername',
        'revoked_by_username',
      ],
    };
    const sources = [item, item.asset, item.additionalInfo].filter(
      (src) => src && typeof src === 'object',
    );
    for (const source of sources) {
      for (const key of keySets[kind]) {
        const name = this.formatActorValue(source[key]);
        if (name !== '-') return name;
      }
    }
    if (kind === 'approved') {
      const ownerName = this.formatActorValue(item.owner ?? item.asset?.owner);
      if (ownerName !== '-') return ownerName;
    }
    return '-';
  }

  private formatActorValue(raw: unknown): string {
    if (raw == null || raw === '') return '-';
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      return trimmed || '-';
    }
    if (typeof raw !== 'object') return '-';
    const person = raw as Record<string, unknown>;
    const first = String(
      person['firstName'] ??
        person['first_name'] ??
        person['rejectedByFirstName'] ??
        person['rejected_by_first_name'] ??
        person['ownerFirstName'] ??
        person['owner_first_name'] ??
        '',
    ).trim();
    const last = String(
      person['lastName'] ??
        person['last_name'] ??
        person['rejectedByLastName'] ??
        person['rejected_by_last_name'] ??
        person['ownerLastName'] ??
        person['owner_last_name'] ??
        '',
    ).trim();
    const combined = [first, last].filter(Boolean).join(' ').trim();
    if (first && last && first.toLowerCase() === last.toLowerCase()) {
      return first;
    }
    if (combined) return combined;
    const fallback = String(
      person['name'] ??
        person['displayName'] ??
        person['display_name'] ??
        person['username'] ??
        person['userName'] ??
        person['email'] ??
        '',
    ).trim();
    return fallback || '-';
  }

  /** Status display label: Pending, Granted, etc. */
  getStatusLabel(status: string): string {
    if (!status) return '-';
    const s = (status + '').toUpperCase();
    if (s === 'GRANTED') return 'Granted';
    if (s === 'PUBLISHED' || s === 'LIVE' || s === 'ACTIVE') return 'Active';
    if (s === 'PENDING') return 'Pending';
    if (s === 'DRAFT') return 'Draft';
    if (s === 'REJECTED' || s === 'FAILED' || s === 'DENIED') return 'Rejected';
    if (s === 'WITHDRAWN') return 'Withdrawn';
    if (s === 'REVOKED') return 'Revoked';
    return status;
  }

  /** CSS class for status chip (Pending = yellow, Granted = green). */
  getStatusChipClass(status: string): string {
    if (!status) return 'contribution-chip contribution-chip--status-default';
    const s = (status + '').toUpperCase();
    if (s === 'GRANTED' || s === 'PUBLISHED' || s === 'LIVE' || s === 'ACTIVE') return 'contribution-chip contribution-chip--status-active';
    if (s === 'PENDING') return 'contribution-chip contribution-chip--status-pending';
    if (s === 'DRAFT') return 'contribution-chip contribution-chip--status-draft';
    if (s === 'REJECTED' || s === 'FAILED' || s === 'DENIED' || s === 'REVOKED') return 'contribution-chip contribution-chip--status-rejected';
    return 'contribution-chip contribution-chip--status-default';
  }

  /** Convert string to Pascal Case (e.g., "DOWNLOAD" -> "Download"). */
  private toPascalCase(str: string): string {
    if (!str || typeof str !== 'string') return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  /** Collapse all child rows (e.g. when switching status tabs). */
  collapseExpandedRows(): void {
    this.rowExpansion.collapseAll();
    this.closeActionDropdown();
  }

  setRequestStatusTab(status: RequestStatusFilter): void {
    if (this.selectedRequestStatus === status) {
      return;
    }
    this.collapseExpandedRows();
    this.selectedRequestStatus = status;
    this.assetRequestEmptyMessage =
      status === 'REVOKED' ? 'No revoked asset requests found' : 'No asset requests found';
    this.syncAssetRequestColumns();
    this.assetRequestPagination = { ...this.assetRequestPagination, currentPage: 1 };
    this.syncRequestStatusQueryParam();
    this.loadAssetRequests();
  }

  /** Restore Pending / Rejected / Granted (and the other status tabs) after returning from Conversation. */
  private applyRequestStatusFromUrl(): void {
    const status = this.parseRequestStatus(
      this.route.snapshot.queryParamMap.get('requestStatus'),
    );
    if (!status) return;
    this.selectedRequestStatus = status;
    this.assetRequestEmptyMessage =
      status === 'REVOKED'
        ? 'No revoked asset requests found'
        : 'No asset requests found';
  }

  private parseRequestStatus(value: string | null): RequestStatusFilter | null {
    if (!value) return null;
    const status = value.toUpperCase() as RequestStatusFilter;
    return this.requestStatusTabs.some((tab) => tab.value === status) ? status : null;
  }

  /** Keep the active status tab in the URL so Conversation's back link can return to it. */
  private syncRequestStatusQueryParam(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        requestStatus:
          this.selectedRequestStatus === 'ALL' ? null : this.selectedRequestStatus,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** Whether Approve/Reject are enabled (Pending tab, or pending rows on All tab). */
  isApproveRejectDisabled(row: AssetRequestData): boolean {
    if (this.selectedRequestStatus === 'PENDING') {
      return false;
    }
    if (this.selectedRequestStatus === 'ALL') {
      const status = (row?.assetStatus ?? '').toString().toUpperCase().trim();
      return status !== 'PENDING';
    }
    return true;
  }

  /** Whether to show an action in the Asset Request dropdown based on active status tab. */
  showAssetRequestAction(action: 'view' | 'approve' | 'reject'): boolean {
    switch (action) {
      case 'view':
        return true;
      case 'approve':
      case 'reject':
        return this.selectedRequestStatus === 'PENDING' || this.selectedRequestStatus === 'ALL';
      default:
        return false;
    }
  }

  /** Normalize API item to AssetRequestData; handles nested structure and type formatting. */
  private mapItemToAssetRequest(item: any): AssetRequestData {
    const asset = item.asset || {};
    const user = item.user || {};
    const additionalInfo = item.additionalInfo || {};
    
    const assetName = asset.assetName || asset.asset_name || item.assetName || item.asset_name || item.name || '';
    
    const rawType = asset.assetType || asset.asset_type || item.assetType || item.asset_type || item.type || '';
    const assetTypeRaw = Array.isArray(rawType) ? (rawType[0] ?? '') : rawType;
    const assetTypeStr = typeof assetTypeRaw === 'string' ? assetTypeRaw : '';
    
    const requestTypeRaw = asset.requestType || item.requestType || item.activity || item.action || '';
    const requestType = requestTypeRaw ? this.toPascalCase(requestTypeRaw) : '';
    
    const statusRaw = asset.requestStatus || item.requestStatus || item.assetStatus || item.asset_status || item.status || 'PENDING';
    
    const firstName = (user.consumerFirstName || user.consumer_first_name || user.firstName || user.first_name || '').toString().trim();
    const lastName  = (user.consumerLastName  || user.consumer_last_name  || user.lastName  || user.last_name  || '').toString().trim();
    const named = this.formatActorValue({ firstName, lastName });
    const requestedBy = (named !== '-' ? named : '') ||
                        item.requestedBy || item.requested_by || item.userName || item.user_name || '';

    const consumerEmail = user.consumerEmail || user.consumer_email || user.email ||
                          item.consumerEmail || item.consumer_email || item.email || '';

    // Prefer asset organisation from the access-request payload (not the consumer org).
    const organisationName =
      asset.organization ||
      asset.organisation ||
      asset.organisationName ||
      asset.organisation_name ||
      item.organisationName ||
      item.organisation_name ||
      item.organization ||
      item.organisation ||
      item.orgName ||
      item.org_name ||
      '';
    
    const purpose = additionalInfo.purposeOfRequest || additionalInfo.purpose_of_request || 
                   item.purpose || item.reason || '';
    
    const requestedAt = item.createdAt || item.created_at || '';
    const expiresAt = asset.expiryAt || asset.expiry_at ||
                     item.expiryAt || item.expiry_at ||
                     item.expiresAt || item.expires_at || '';
    
    return {
      assetName: displayValueOrDash(assetName),
      assetType: displayValueOrDash(this.getAssetTypeDisplayLabel(assetTypeStr) || assetTypeStr),
      requestType: displayValueOrDash(requestType),
      assetStatus: displayValueOrDash(statusRaw),
      requestedBy: displayValueOrDash(requestedBy),
      consumerEmail: displayValueOrDash(consumerEmail),
      organisationName: displayValueOrDash(organisationName),
      purpose: displayValueOrDash(purpose),
      requestedAt: requestedAt,
      expiresAt: expiresAt,
      action: '',
      originalData: item
    };
  }

  /** When the current page is past the last page (e.g. after approving the only item on page 2), reload on the last valid page. */
  private getMaxPage(totalItems: number, pageSize: number): number {
    return Math.max(1, Math.ceil(totalItems / pageSize));
  }

  async loadAssetRequests(): Promise<void> {
    this.assetRequestLoading = true;
    
    const mockData = this.assetRequestData.filter(d => d.originalData?.id?.startsWith('mock-'));
    let retryOnPreviousPage = false;
    
    try {
      const page = this.assetRequestPagination.currentPage;
      const size = this.assetRequestPagination.pageSize;
      const sort = `${this.getApiSortField(this.currentSort.field)}:${this.currentSort.order}`;
      
      const isExpired = this.selectedRequestStatus === 'EXPIRED';
      const requestStatus =
        this.selectedRequestStatus === 'ALL' || isExpired ? undefined : this.selectedRequestStatus;

      const response = await this.aclApdApiService.getAssetRequestForAnOrganisation(
        page,
        size,
        sort,
        requestStatus,
        isExpired,
      );

      console.log('Asset Request API Response:', response);

      let mappedData: AssetRequestData[] = [];
      let totalItems = 0;

      if (response) {
        if (response['result'] && Array.isArray(response['result'])) {
          console.log('Mapping result array, items count:', response['result'].length);
          mappedData = response['result'].map((item: any) => {
            console.log('Mapping item:', item);
            const mapped = this.mapItemToAssetRequest(item);
            console.log('Mapped result:', mapped);
            return mapped;
          });
          totalItems = response['paginationInfo']?.totalCount || response['totalCount'] || response['total'] || mappedData.length;
        } else if (response['data'] && Array.isArray(response['data'])) {
          mappedData = response['data'].map((item: any) => this.mapItemToAssetRequest(item));
          totalItems = response['paginationInfo']?.totalCount || response['totalCount'] || response['total'] || mappedData.length;
        } else if (Array.isArray(response)) {
          mappedData = response.map((item: any) => this.mapItemToAssetRequest(item));
          totalItems = mappedData.length;
        }
      }

      if (mappedData.length === 0 && mockData.length > 0) {
        this.assetRequestData = mockData;
        this.assetRequestPagination = { 
          ...this.assetRequestPagination, 
          totalItems: mockData.length 
        };
      } else {
        this.assetRequestData = mappedData;
        this.assetRequestPagination = { 
          ...this.assetRequestPagination, 
          totalItems: totalItems 
        };
      }

      const maxPage = this.getMaxPage(this.assetRequestPagination.totalItems, size);
      if (page > maxPage) {
        this.assetRequestPagination = {
          ...this.assetRequestPagination,
          currentPage: maxPage,
        };
        retryOnPreviousPage = true;
      }
    } catch (error) {
      console.error('Error loading asset requests:', error);
      if (mockData.length > 0) {
        this.assetRequestData = mockData;
        this.assetRequestPagination = { ...this.assetRequestPagination, totalItems: mockData.length };
      } else {
        this.assetRequestData = [];
        this.assetRequestPagination = { ...this.assetRequestPagination, totalItems: 0 };
      }
    } finally {
      if (!retryOnPreviousPage) {
        this.assetRequestLoading = false;
      }
    }

    if (retryOnPreviousPage) {
      await this.loadAssetRequests();
    }
  }

  onAssetRequestPageChange(page: number): void {
    this.collapseExpandedRows();
    this.assetRequestPagination = { ...this.assetRequestPagination, currentPage: page };
    this.loadAssetRequests();
  }

  onAssetRequestPageSizeChange(pageSize: number): void {
    this.collapseExpandedRows();
    this.assetRequestPagination = { ...this.assetRequestPagination, pageSize: pageSize, currentPage: 1 };
    this.loadAssetRequests();
  }

  onAssetRequestSortChange(sort: { column: string; direction: 'asc' | 'desc' }): void {
    this.collapseExpandedRows();
    this.currentSort = { field: sort.column, order: sort.direction };
    this.assetRequestPagination = { ...this.assetRequestPagination, currentPage: 1 };
    this.loadAssetRequests();
  }

  private getApiSortField(field: string): string {
    const fieldMapping: { [key: string]: string } = {
      assetName: 'assetName',
      requestType: 'requestType',
      assetStatus: 'requestStatus',
      requestDate:
        this.selectedRequestStatus === 'GRANTED' || this.selectedRequestStatus === 'EXPIRED'
          ? 'expiryAt'
          : 'createdAt',
    };
    return fieldMapping[field] || field;
  }

  private readonly actionDropdownOverlay = new ActionDropdownOverlayController();

  toggleActionDropdown(event: Event, row: AssetRequestData): void {
    event.stopPropagation();
    const rowId = row.originalData?.requestId || row.originalData?.id || JSON.stringify(row);

    if (this.openDropdownId === rowId) {
      this.closeActionDropdown();
      return;
    }
    this.rowExpansion.collapseAll();
    const button = event.currentTarget as HTMLElement;
    this.openDropdownId = rowId;
    this.actionDropdownOverlay.open({
      anchor: button,
      itemCount: this.canConverse(row) ? 4 : 3,
      minWidth: 160,
      onPosition: (pos) => {
        this.dropdownPosition = { top: pos.top, left: pos.left, right: pos.right };
        this.cdr.markForCheck();
      },
      onAnchorDetached: () => this.closeActionDropdown(),
    });
  }

  closeActionDropdown(): void {
    this.actionDropdownOverlay.close();
    this.openDropdownId = null;
  }

  isDropdownOpen(row: AssetRequestData): boolean {
    const rowId = row.originalData?.requestId || row.originalData?.id || JSON.stringify(row);
    return this.openDropdownId === rowId;
  }

  /** Index in assetRequestData of the row whose action dropdown is open; null when none. */
  get openAssetRequestDropdownRowIndex(): number | null {
    if (this.openDropdownId == null) {
      return null;
    }
    const idx = this.assetRequestData.findIndex(
      (r) => (r?.originalData?.requestId ?? r?.originalData?.id ?? JSON.stringify(r)) === this.openDropdownId
    );
    return idx >= 0 ? idx : null;
  }

  /**
   * Toggle row expansion for child rows.
   * Only one row can be expanded at a time; opening a row collapses any other
   * currently open row and also closes any open action dropdown.
   */
  toggleRowExpansion(row: AssetRequestData): void {
    const rowId = row.originalData?.requestId || row.originalData?.id || JSON.stringify(row);
    if (!this.rowExpansion.isExpanded(rowId)) {
      this.closeActionDropdown();
    }
    this.rowExpansion.toggle(rowId);
  }

  /**
   * Check if a row is expanded
   */
  isRowExpanded(row: AssetRequestData): boolean {
    const rowId = row.originalData?.requestId || row.originalData?.id || JSON.stringify(row);
    return this.rowExpansion.isExpanded(rowId);
  }

  /**
   * Get the bound isRowExpanded function for passing to child component
   */
  getIsRowExpanded(): (row: any) => boolean {
    return (row: any) => this.isRowExpanded(row);
  }

  handleAction(action: string, row: AssetRequestData): void {
    if ((action === 'approve' || action === 'reject') && this.isApproveRejectDisabled(row)) {
      return;
    }

    this.closeActionDropdown();

    if (action === 'approve') {
      this.selectedRequest = row;
      this.expirationDate = null;
      setTimeout(() => {
        this.isApproveModalVisible = true;
        this.cdr.detectChanges();
      }, 0);
    } else if (action === 'reject') {
      this.rejectAssetRequest(row);
    } else if (action === 'view') {
      this.viewAssetRequest(row);
    } else if (action === 'conversation') {
      this.openConversation(row);
    }
  }

  /** Conversation stays with the asset owner after withdraw, approval, revoke, and expiry. */
  canConverse(row: AssetRequestData): boolean {
    if (this.workspaceContext.isDelegateSession()) return false;
    const requestId = this.getConversationRequestId(row);
    const status = this.effectiveConversationStatus(row);
    if (
      !this.conversationRoute ||
      !requestId ||
      !canOpenConversation(status, row?.expiresAt) ||
      !this.conversationService.isAssetOwner(row.originalData?.owner?.ownerId)
    ) {
      return false;
    }
    return true;
  }

  /** A granted request that has passed its expiry is treated as expired. */
  private effectiveConversationStatus(row: AssetRequestData): string {
    const status = (row?.assetStatus ?? '').trim().toLowerCase();
    if (status === 'revoked' || status === 'expired') {
      return status;
    }
    const grantExpired =
      (status === 'granted' || status === 'approved' || status === 'active') &&
      !isAccessStillActive(row?.expiresAt);
    if (grantExpired || this.selectedRequestStatus === 'EXPIRED') {
      return 'expired';
    }
    return status;
  }

  private getConversationRequestId(row: AssetRequestData): string {
    return row.originalData?.requestId || row.originalData?.id || '';
  }

  private openConversation(row: AssetRequestData): void {
    const queryParams: Record<string, string> = { tab: 'assetRequest' };
    if (this.selectedRequestStatus !== 'ALL') {
      queryParams['requestStatus'] = this.selectedRequestStatus;
    }
    void this.router.navigate([this.conversationRoute, this.getConversationRequestId(row), 'conversation'], {
      queryParams,
      state: {
        assetName: row.assetName,
        purpose: row.purpose,
        status: this.effectiveConversationStatus(row),
      },
    });
  }

  private viewAssetRequest(row: AssetRequestData): void {
    const original = row.originalData || {};
    const asset = original.asset || {};
    const itemId =
      original.itemId ||
      original.item_id ||
      asset.itemId ||
      asset.item_id;

    if (!itemId) {
      console.warn('Unable to navigate to asset details: missing itemId', row);
      this.toasterService.error('Unable to view asset: missing item ID');
      return;
    }

    const typeLabel = (row.assetType || '').toLowerCase();
    let routeSegment: string | null = null;

    if (typeLabel === 'dataset') {
      routeSegment = 'dataset';
    } else if (typeLabel === 'model') {
      routeSegment = 'model';
    } else if (typeLabel === 'app') {
      routeSegment = 'usecase';
    } else {
      const rawType = asset.assetType || asset.asset_type || asset.type || original.type || '';
      const rawTypeStr = Array.isArray(rawType) ? (rawType[0] ?? '') : rawType;
      const rawTypeLower = (rawTypeStr + '').toLowerCase();

      if (rawTypeLower.includes('databank') || rawTypeLower.includes('database')) {
        routeSegment = 'dataset';
      } else if (rawTypeLower.includes('aimodel') || rawTypeLower.includes('model')) {
        routeSegment = 'model';
      } else if (rawTypeLower.includes('app') || rawTypeLower.includes('usecase')) {
        routeSegment = 'usecase';
      }
    }

    if (routeSegment) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([`/${routeSegment}`, itemId])
      );
      window.open(url, '_blank');
    } else {
      console.warn('Unknown asset type for navigation:', row.assetType, row);
      this.toasterService.error('Unable to determine asset type for navigation');
    }
  }

  private async approveAssetRequest(row: AssetRequestData): Promise<void> {
    const requestId = row.originalData?.requestId || row.originalData?.id;

    if (!requestId) {
      console.warn('Unable to approve asset request: missing request ID', row);
      this.toasterService.error('Unable to approve asset request: missing request ID');
      return;
    }

    try {
      if (!this.expirationDate?.startDate) {
        console.error('Expiry date is required to approve asset request');
        return;
      }

      const expiryAt = this.formatExpiryAt(this.expirationDate.startDate);

      const payload = {
        requestId: requestId,
        status: 'granted',
        expiryAt: expiryAt,
      };

      await this.aclApdApiService.updateAccessRequest(requestId, payload);
      
      this.toasterService.success('Asset request has been approved successfully');
      this.closeApproveModal();
      this.loadAssetRequests();
    } catch (error) {
      console.error('Error approving asset request:', error);
      this.toasterService.error('Failed to approve asset request. Please try again.');
    }
  }

  onApproveConfirm(): void {
    if (!this.selectedRequest) {
      return;
    }
    this.approveAssetRequest(this.selectedRequest);
  }

  closeApproveModal(): void {
    this.isApproveModalVisible = false;
    this.selectedRequest = null;
    this.expirationDate = null;
  }

  isExpiryValid(): boolean {
    if (!this.expirationDate?.startDate) return false;
    const d = new Date(this.expirationDate.startDate);
    d.setHours(23, 59, 59, 999);
    return d.getTime() > Date.now();
  }

  private formatExpiryAt(date: Date): string {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return istToUtc(`${year}-${month}-${day}`, '23:59:00');
  }

  private async rejectAssetRequest(row: AssetRequestData): Promise<void> {
    const requestId = row.originalData?.requestId || row.originalData?.id;

    if (!requestId) {
      console.warn('Unable to reject asset request: missing request ID', row);
      this.toasterService.error('Unable to reject asset request: missing request ID');
      return;
    }

    try {
      const payload = {
        requestId: requestId,
        status: 'rejected'
      };

      await this.aclApdApiService.updateAccessRequest(requestId, payload);
      
      this.toasterService.success('Asset request has been rejected successfully');
      this.loadAssetRequests();
    } catch (error) {
      console.error('Error rejecting asset request:', error);
      this.toasterService.error('Failed to reject asset request. Please try again.');
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.openDropdownId !== null && !target.closest('.action-cell-wrapper')) {
      this.closeActionDropdown();
    }
  }

  ngOnDestroy(): void {
    this.closeActionDropdown();
  }
}
